import { useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { RefreshButton } from "./RefreshButton";
import { fmt } from "../format";
import type { DlqDepths, DlqInspection } from "../types";

/** Friendly labels for the three terminal DLQs (topology names are the API contract). */
const QUEUE_LABEL: Record<string, string> = {
  "scrape.jobs.dlq": "Scrape jobs",
  "scrape.broken.dlq": "Broken events",
  "result.ready.dlq": "Result delivery",
};

/**
 * Dead-letter viewer (operator-only, READ-ONLY). Lists the three DLQs with live depth,
 * and lets an operator sample one queue's messages (decoded payload + x-death metadata).
 * Peeking is NON-DESTRUCTIVE — the API requeues everything it reads. Purge/replay is the
 * break-glass admin-cli's job (drain-queue), deliberately not exposed here.
 */
export function DeadLetters() {
  const [auto, setAuto] = useState(false);
  const interval = auto ? 10_000 : undefined;
  const { data, loading, error, reload } = useAsync<DlqDepths>(() => api("/dlq"), [], interval);
  const [selected, setSelected] = useState<string | null>(null);

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  const queues = data?.queues ?? [];
  const totalDead = queues.reduce((sum, q) => sum + q.messageCount, 0);

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Dead letters</h1>
        <div className="row-gap">
          <label className="inline muted">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> auto
          </label>
          <RefreshButton onClick={reload} />
        </div>
      </div>

      {totalDead === 0 && (
        <div className="empty">Nothing dead-lettered. Every message has been processed.</div>
      )}

      <div className="dlq-cards">
        {queues.map((q) => (
          <button
            key={q.name}
            className={`dlq-card${selected === q.name ? " active" : ""}${q.messageCount > 0 ? " has-dead" : ""}`}
            onClick={() => setSelected(selected === q.name ? null : q.name)}
          >
            <div className="dlq-count">{q.messageCount}</div>
            <div className="dlq-label">{QUEUE_LABEL[q.name] ?? q.name}</div>
            <div className="dlq-queue mono muted">{q.name}</div>
          </button>
        ))}
      </div>

      {selected && <DlqMessages queue={selected} />}
    </div>
  );
}

/** Sampled messages for one DLQ. Non-destructive peek (the API requeues what it reads). */
function DlqMessages({ queue }: { queue: string }) {
  const { data, loading, error } = useAsync<DlqInspection>(
    () => api(`/dlq/${encodeURIComponent(queue)}?limit=50`),
    [queue],
  );

  if (loading && !data) return <div className="muted pad">Sampling {queue}…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  const msgs = data?.sampled ?? [];

  return (
    <div className="stack-sm">
      <div className="muted">
        Showing {msgs.length} of {data?.messageCount ?? 0} in <span className="mono">{queue}</span>.
        Peeking does not remove anything — use the admin CLI to drain.
      </div>
      {msgs.length === 0 && <div className="empty">This queue is empty.</div>}
      {msgs.map((m, i) => (
        <details key={i} className="dlq-msg">
          <summary>
            <span className="mono">{m.routingKey}</span>
            {m.firstDeathReason && <span className="pill failed">{m.firstDeathReason}</span>}
            {m.deathCount > 1 && <span className="muted"> ×{m.deathCount}</span>}
            {m.firstDeathAt && <span className="muted nowrap"> · {fmt(m.firstDeathAt)}</span>}
          </summary>
          <div className="dlq-msg-body">
            {m.firstDeathQueue && (
              <div className="muted">
                first died in <span className="mono">{m.firstDeathQueue}</span>
              </div>
            )}
            <pre className="code-block">
              {m.raw ? String(m.payload) : JSON.stringify(m.payload, null, 2)}
            </pre>
          </div>
        </details>
      ))}
    </div>
  );
}
